import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, ViewChild, inject } from "@angular/core";
import { useTranslationNamespace } from "@logex/framework/lg-localization";
import { LogexPivotService } from "@logex/framework/lg-pivot";
import { LgPivotTableComponent } from "@logex/framework/lg-pivot-table";
import { IColumnFilterDictionary, IFilterOption, LgSimpleChanges } from "@logex/framework/types";

import { AssignApplicationRolesPivot } from "./pivot/assign-application-roles-pivot";
import {
    AssignRolesPivotProduct,
    AssignRolesPivotRole
} from "./pivot/assign-application-roles-pivot.types";
import { SharedDefinitions } from "@mrdm-self-service-frontend/shared/data-access-definitions";
import { ApplicationRole, AvailableApplicationRole, BusinessRole } from "../../types";
import { getAppRoleKey } from "../../helpers/application-role-key";
import { keyBy } from "lodash";
import { CheckState } from "@mrdm-self-service-frontend/shared/types";

@Component({
    selector: "app-assign-application-roles",
    templateUrl: "./assign-application-roles.component.html",
    styleUrls: ["./assign-application-roles.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [useTranslationNamespace("APP._Shared.AssignApplicationRoles")],
    standalone: false
})
export class AssignApplicationRolesComponent implements OnChanges {
    private _pivotService = inject(LogexPivotService);
    _definitions = inject(SharedDefinitions);
    _pivot = inject(AssignApplicationRolesPivot);

    @ViewChild("applicationRolesTable", { static: false })
    _applicationRolesTable!: LgPivotTableComponent;

    @Input() selectedApplicationRoles: ApplicationRole[] = [];
    @Input() availableRoles: AvailableApplicationRole[] = [];
    @Input() canSelectInternalRoles = false;
    @Input() selectedBusinessRoles: BusinessRole[] = [];
    @Input() showOnlySelected = false;

    @Output() readonly selectedApplicationRolesChange: EventEmitter<AvailableApplicationRole[]> =
        new EventEmitter();

    _allProductsExpanded = false;

    _applicationInstancesFilterSource!: IFilterOption[];
    _productsFilterSource!: IFilterOption[];
    _applicationRolesFilterSource!: IFilterOption[];

    _filteredProducts: IColumnFilterDictionary = { $empty: true };
    _filteredApplicationInstances: IColumnFilterDictionary = { $empty: true };
    _filteredApplicationRoles: IColumnFilterDictionary = { $empty: true };
    _showOnlySelected = false;

    _availableRolesDictionary: Record<string, AvailableApplicationRole> = {};
    _initiallySelectedDictionary: Record<string, ApplicationRole> = {};
    _assignedByBusinessRole: Record<string, boolean> = {};

    _pivotData: AssignRolesPivotProduct[] = [];
    _filteredPivotData: AssignRolesPivotProduct[] = [];

    _checkState: CheckState = CheckState.Empty;
    _currentSelection: AssignRolesPivotRole[] = [];
    _currentVisibleSelection: AssignRolesPivotRole[] = [];

    ngOnChanges(changes: LgSimpleChanges<AssignApplicationRolesComponent>): void {
        if (changes.showOnlySelected) {
            this._showOnlySelected = changes.showOnlySelected.currentValue;
        }
        if (
            changes.availableRoles ||
            changes.canSelectInternalRoles ||
            changes.selectedBusinessRoles
        ) {
            this._setAvailableRolesDictionary();
            this._setSelectedDictionary();
            this._setSelectedBusinessRoleDictionary();
            this._buildPivot();
            this._refilter();
            this.selectedApplicationRolesChange.emit(this._currentSelection);
        }
    }

    _toggleRole(role: AssignRolesPivotRole): void {
        if (role.disabled) return;
        role.selected = !role.selected;
        this._setCurrentSelection();
        this._setCheckState();
        this.selectedApplicationRolesChange.emit(this._currentSelection);
    }

    _toggleAllVisible(toggle: boolean): void {
        this._pivotService.eachLeafFiltered(
            this._pivot.definition,
            this._filteredPivotData,
            false,
            role => {
                if (!role.disabled) {
                    role.selected = toggle;
                }
            }
        );
        this._setCurrentSelection();
        this._setCheckState();

        this.selectedApplicationRolesChange.emit(this._currentSelection);
    }

    _expandAll(expand: boolean): void {
        this._pivotData.forEach(product => {
            product.$expanded = expand;
            product.applicationInstances.forEach(appInstance => {
                appInstance.$expanded = expand;
            });
        });

        this._allProductsExpanded = expand;
        this._applicationRolesTable?.render();
    }

    _expandSingle(product: AssignRolesPivotProduct): void {
        product.applicationInstances.forEach(i => {
            i.$expanded = true;
        });

        this._allProductsExpanded = !this._filteredPivotData.some(x => !x.$expanded);
        this._applicationRolesTable?.render();
    }

    _refilter(): void {
        if (this._isSomeFilterActive) {
            this._expandAll(true);
        }

        const unordered = this._pivotService.filter(this._pivot.definition, this._pivotData, this);

        this._filteredPivotData = this._pivotService.orderByFiltered(
            this._pivot.definition,
            unordered,
            ""
        );

        this._setFilterSources();
        this._setCurrentSelection();
        this._setCheckState();
    }

    private get _isSomeFilterActive(): boolean {
        return (
            (this._filteredProducts && !this._filteredProducts.$empty) ||
            (this._filteredApplicationInstances && !this._filteredApplicationInstances.$empty) ||
            (this._filteredApplicationRoles && !this._filteredApplicationRoles.$empty) ||
            this._showOnlySelected
        );
    }

    private _setAvailableRolesDictionary(): void {
        this._availableRolesDictionary = keyBy(this.availableRoles, role => getAppRoleKey(role));
    }

    private _setSelectedDictionary(): void {
        this._initiallySelectedDictionary = keyBy(this.selectedApplicationRoles, role =>
            getAppRoleKey(role)
        );
    }

    private _setSelectedBusinessRoleDictionary(): void {
        this._assignedByBusinessRole = {};
        this.selectedBusinessRoles.forEach(br =>
            br.applicationRoles.forEach(ar => {
                this._assignedByBusinessRole[getAppRoleKey(ar)] = true;
            })
        );
    }

    private _setCurrentSelection(): void {
        this._currentSelection = [];
        this._currentVisibleSelection = [];

        this._pivotService.eachLeaf(this._pivot.definition, this._pivotData, false, role => {
            if (role.selected) this._currentSelection.push(role);
        });

        this._pivotService.eachLeafFiltered(
            this._pivot.definition,
            this._filteredPivotData,
            false,
            role => {
                if (role.selected) this._currentVisibleSelection.push(role);
            }
        );
    }

    private _setCheckState(): void {
        const selectedCount = this._currentVisibleSelection.length;
        const visibleCount = this._filteredPivotData.length;
        this._checkState = selectedCount > 0 ? (selectedCount !== visibleCount ? 2 : 1) : 0;
    }

    private _buildPivot(): void {
        this._pivotData = this._pivotService.build(
            this._pivot.definition,
            this.availableRoles,
            null,
            this
        );
    }

    private _setFilterSources(): void {
        this._setApplicationsSource();
        this._setProductsSource();
        this._setApplicationRolesSource();
    }

    private _setProductsSource(): void {
        const products = this._gatherFilterIds("productCode", "applicationRoles");
        this._productsFilterSource = products
            .map(p => {
                return {
                    id: p,
                    name: this._definitions.product[p]?.name,
                    order: p
                };
            })
            .sort((a, b) => (a.name > b.name ? 1 : -1));
    }

    private _setApplicationsSource(): void {
        const appInstances = this._gatherFilterIds("applicationInstanceCode", "applicationRoles");
        this._applicationInstancesFilterSource = appInstances
            .map(ai => {
                return {
                    id: ai,
                    name: this._definitions.applicationInstance[ai]?.name,
                    order: ai
                };
            })
            .sort((a, b) => (a.name > b.name ? 1 : -1));
    }

    private _setApplicationRolesSource(): void {
        const appRoles = this._gatherFilterIds("applicationRoleCode", "applicationRoles");
        this._applicationRolesFilterSource = appRoles
            .map(a => {
                return {
                    id: a,
                    name: this._definitions.applicationRole[a].name,
                    order: a
                };
            })
            .sort((a, b) => (a.name > b.name ? 1 : -1));
    }

    private _gatherFilterIds<T extends string>(
        property: string,
        levelId: string,
        ignored?: string | string[]
    ): T[] {
        const level = this._pivot.definition.$levels[levelId].$levelIndex;
        const result = this._pivotService.gatherFilterOptionIdsFromLevel<T>(
            this._pivot.definition,
            this._pivotData,
            property,
            ignored || [property],
            level,
            this
        );
        return result;
    }
}
